import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import React, { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { clientOnboardingSchema, clientOnboardingType } from "../../validator";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import GoogleIcon from "@/components/icons/GoogleIcon";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { auth } from "@/clients/firebase";
import { googleRegister, register } from "@/http";
import { useMutation } from "@tanstack/react-query";
import useCurrentUserStore from "@/store";
import { LoaderCircle } from "lucide-react";
import useSonner from "@/hooks/use-sonner";
import { useNavigate } from "react-router-dom";
type PropTypes = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<clientOnboardingType, any, undefined>;
  nextTrigger: VoidFunction;
};

const Step1Content: React.FC<PropTypes> = (props) => {
  const step1Data = props.form.watch("step1");
  const { addToast } = useSonner();
  const navigate = useNavigate();
  const [isSignInWithGoogle, setIsSignInWithGoogle] = useState<boolean>(false);
  const { setDashboardAccessToken, setCurrentUser, setEmail } =
    useCurrentUserStore();
  const registerUser = useMutation({
    mutationFn: register,
    onSuccess: (res) => {
      if (res?.data?.data?.result?.magic_link) {
        // window.open(import.meta.env.VITE_DASHBOARD_URL, "_blank");
        props.form.clearErrors();
        props.form.reset();
        addToast({
          message:
            res.data.data.message ||
            `Account Already exists. We have sent the login link at ${step1Data.email}`,
          type: "info",
        });
      } else {
        setEmail(res?.data?.data?.result?.email || "");
        props.form.clearErrors();
        props.nextTrigger();
        addToast({
          message:
            res.data.data.message ||
            `Account created successfully. We have sent the otp at ${step1Data.email}`,
          type: "success",
        });
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any) => {
      addToast({
        message: err?.response?.data?.data?.message || "Something went wrong !",
        type: "error",
      });
    },
  });

  const registerUserWithGoogle = useMutation({
    mutationFn: googleRegister,
    onSuccess: (res) => {
      if (res?.data?.data?.result?.magic_link) {
        // window.open(import.meta.env.VITE_DASHBOARD_URL, "_blank");
        props.form.clearErrors();
        props.form.reset();
        setIsSignInWithGoogle(false);
        addToast({
          message:
            res.data.data.message ||
            `Account Already exists. We have sent the login link at ${step1Data.email}`,
          type: "info",
        });
      } else {
        setIsSignInWithGoogle(false);
        setDashboardAccessToken(res.data.data.result.access_token || "");
        setCurrentUser(res.data.data.result);
        addToast({
          message: res.data.data.message || `Account created successfully.`,
          type: "success",
        });
        props.form.clearErrors();
        navigate("/app-creation");
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any) => {
      setIsSignInWithGoogle(false);
      addToast({
        message: err?.response?.data?.data?.message || "Something went wrong !",
        type: "error",
      });
    },
  });

  const handleNextClick = () => {
    const resolve = clientOnboardingSchema.shape.step1.safeParse(step1Data);
    if (resolve.success) {
      registerUser.mutate({
        email: step1Data.email,
        contact_number: "123456789",
      });
    }

    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      event: "account_creation_step1",
      email: step1Data.email,
    });
  };

  const handleGoogleSignIn = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    const provider = new GoogleAuthProvider();
    setIsSignInWithGoogle(true);
    provider.setCustomParameters({
      prompt: "select_account",
    });

    signInWithPopup(auth, provider)
      .then(async (result) => {
        const token = await result.user.getIdToken();

        registerUserWithGoogle.mutate({
          token,
        });
      })
      .catch(() => {
        setIsSignInWithGoogle(false);
        addToast({
          message: "Failed to sign in with Google. Please try again.",
          type: "error",
        });
      });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <h2 className="font-semibold text-[28px]/[32px]">Let's get started</h2>
        <p className="text-sm text-muted-foreground">
          Start building AI-powered apps in minutes.
        </p>
      </div>

      {/* Email */}
      <FormField
        control={props.form.control}
        name="step1.email"
        defaultValue={step1Data.email}
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input
                type="email"
                placeholder="Enter your email address"
                className={cn("w-full", {
                  "border-destructive focus-visible:ring-transparent":
                    fieldState.error,
                })}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* next button */}
      <Button
        type="submit"
        disabled={isSignInWithGoogle || registerUser.isPending}
        className="w-full font-normal"
        onClick={handleNextClick}
      >
        {registerUser.isPending && <LoaderCircle className="animate-spin" />}
        Continue
      </Button>

      <div className="grid grid-cols-[45%_10%_45%] text-sm items-center text-center text-muted-foreground">
        <Separator />
        <p>OR</p>
        <Separator />
      </div>

      <Button
        variant="outline"
        type="button"
        disabled={isSignInWithGoogle || registerUser.isPending}
        onClick={handleGoogleSignIn}
        className="w-full font-bold flex items-center justify-center gap-3"
      >
        {isSignInWithGoogle && <LoaderCircle className="animate-spin" />}
        <GoogleIcon />
        Continue with Google
      </Button>
    </div>
  );
};

export default Step1Content;
